import { config } from '@/shared/config/env';

/**
 * Left-hand visual panel on /login + /signup. 1:1 port of the
 * Banner / Banner1 / TitleAndSubtitle / Quote functions from
 * `~/Downloads/BIASafe AI - Quant Model Portfolio Designer (2)/
 *  src/app/components/SignIn.tsx`.
 *
 * Structure (Figma terms):
 *  - Banner1 (sticky, w-[45%], h-screen, p-[8px]) wraps...
 *  - Banner (rounded-[calc(var(--corner-radius)*3)], overflow-clip) which contains:
 *      - Background image, scaled 2116px wide, rotated 180deg + scaleY(-1)
 *      - Quote block at top-left: "OUR PHILOSOPHY" + 150px hairline rule
 *      - TitleAndSubtitle block at bottom-left: 72px serif + tagline
 *
 * Hidden below md breakpoint to match the export's `hidden md:flex`.
 */
const FONT_SERIF = 'var(--font-brand)';
const FONT_SANS = 'var(--font-brand)';

export function AuthHeroPanel() {
  return (
    <div
      className="hidden md:flex bg-white box-border content-stretch gap-[10px] items-start p-[8px] relative shrink-0 w-[45%] min-w-[45%] max-w-[45%] h-screen sticky top-0"
      data-name="banner"
    >
      <div
        className="basis-0 grow h-full min-h-px min-w-px overflow-clip relative rounded-[calc(var(--corner-radius)*3)] shrink-0"
        data-name="banner"
      >
        {/* Background bitmap — exact Figma disposition: a fixed 2116px-wide
         * image whose centre sits +360px right of the panel centre (left-0
         * at >=1920px), rotated 180deg and flipped vertically. */}
        <div className="absolute flex h-full items-center justify-center left-[calc(50%+360px)] min-[1920px]:left-0 top-[calc(50%+0.5px)] translate-x-[-50%] min-[1920px]:translate-x-0 translate-y-[-50%] w-[2116px]">
          <div className="flex-none rotate-[180deg] scale-y-[-100%] h-full">
            <div className="h-full relative w-[2116px]">
              <img
                src={`${config.app.basePath}auth-bg-hero.jpg`}
                alt=""
                decoding="async"
                className="absolute inset-0 max-w-none object-center object-cover pointer-events-none size-full"
              />
            </div>
          </div>
        </div>

        {/* TitleAndSubtitle, absolute bottom-left. */}
        <div
          className="absolute content-stretch flex flex-col gap-[16px] items-start leading-[0] left-[48px] bottom-[8%] w-[381px] max-w-[calc(100%-96px)]"
          data-name="title-and-subtitle"
        >
          <div
            className="flex flex-col font-normal justify-center relative shrink-0 text-[72px] text-white tracking-[-2.88px] w-[381px] max-w-full"
            style={{ fontFamily: FONT_SERIF }}
          >
            <p
              className="leading-[72px] text-[72px] font-normal text-white tracking-[-2.88px]"
              style={{
                fontFamily: FONT_SERIF,
                fontVariationSettings: "'CTGR' 100, 'wdth' 100",
              }}
            >
              Quant Strategies, Simplified.
            </p>
          </div>
          <div
            className="flex flex-col justify-center min-w-full not-italic relative shrink-0 text-[16px] text-[rgba(255,255,255,0.88)] w-[min-content]"
            style={{ fontFamily: FONT_SANS }}
          >
            <p className="leading-[26px] text-[#ffffff] text-[16px]">
              Institutional data, governed workflows, and audit-ready
              execution, without the engineering overhead.
            </p>
          </div>
        </div>

        {/* Quote, absolute top-left. */}
        <div
          className="absolute content-stretch flex gap-[20px] items-center left-[48px] top-[48px]"
          data-name="quote"
        >
          <div
            className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[16px] text-[rgba(255,255,255,0.8)] text-nowrap tracking-[2.56px]"
            style={{ fontFamily: FONT_SANS }}
          >
            <p className="leading-[26px] whitespace-pre text-[#ffffff]">
              OUR PHILOSOPHY
            </p>
          </div>
          <div className="h-0 relative shrink-0 w-[150px]">
            <div className="absolute bottom-0 left-0 right-0 top-[-1px]">
              <svg
                className="block size-full"
                fill="none"
                preserveAspectRatio="none"
                viewBox="0 0 150 1"
              >
                <line
                  stroke="white"
                  strokeOpacity="0.8"
                  x2="150"
                  y1="0.5"
                  y2="0.5"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
